	// button.js
import { apiBaseUrl } from '../../backend/config.js';
import { showNotification, updateTotalPointsDisplay, showPopupPoints, clearLEDDisplay } from './notif.js';
import { connectWebSocket, socket, waitForSocketConnection, messageQueue } from './socketU.js';
import { getAuthToken } from './auth.js';
import { t, confirmT, updateElementText } from './translationHelper.js';
import translations from './translations.js';

if (!globalThis.ZTranslations) {
  globalThis.ZTranslations = translations;
}

if (typeof globalThis.$t !== 'function') {
  globalThis.$t = (key, params) => {
	return t(key);
  };
}


function localizeCompanyName(stockObj = {}) {
  const key = stockObj.csvId || stockObj.companyCsvId || stockObj.id || stockObj._id;
  return key ? t(`companies_by_id.${String(key)}.Company_Name`, stockObj.name || '') : (stockObj.name || '');
}

function initializeDeletePortfolioButton() {
  const deletePortfolioButton = document.getElementById('deletePortfolioButton');
  if (deletePortfolioButton) {
	deletePortfolioButton.addEventListener('click', handleDeletePortfolioButtonClick);
  }
}

// At the top of button.js, replace your cache declaration with:
const portfolioCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Add this function to preload on page load:
async function preloadPortfolios() {
  const sessionId = localStorage.getItem('sessionId');
  if (!sessionId) return;
  
  try {
	// Fetch both endpoints in parallel
	const [active, richest] = await Promise.all([
	  fetch(`${apiBaseUrl}/api/portfolios/active`, {
		method: 'POST',
		headers: {
		  'Authorization': `Bearer ${getAuthToken()}`,
		  'Content-Type': 'application/json',
		},
		body: JSON.stringify({ sessionId })
	  }).then(r => r.json()),
	  fetch(`${apiBaseUrl}/api/portfolios/richest`, {
		method: 'POST', 
		headers: {
		  'Authorization': `Bearer ${getAuthToken()}`,
		  'Content-Type': 'application/json',
		},
		body: JSON.stringify({ sessionId })
	  }).then(r => r.json())
	]);
	
	const now = Date.now();
	portfolioCache.set('active', { data: active, timestamp: now });
	portfolioCache.set('richest', { data: richest, timestamp: now });
  } catch (error) {
	console.error('Failed to preload portfolios:', error);
  }
}

async function getCachedPortfolios(sessionId, endpoint = '/api/portfolios/active') {
  const cacheKey = endpoint.includes('richest') ? 'richest' : 'active';
  const cached = portfolioCache.get(cacheKey);
  
  if (cached && (Date.now() - cached.timestamp < CACHE_DURATION)) {
	return cached.data;
  }
  
  // Fallback to API call if cache miss
  const response = await fetch(`${apiBaseUrl}${endpoint}`, {
	method: 'POST',
	headers: {
	  'Authorization': `Bearer ${getAuthToken()}`,
	  'Content-Type': 'application/json',
	},
	body: JSON.stringify({ sessionId })
  });
  
  const data = await response.json();
  portfolioCache.set(cacheKey, { data, timestamp: Date.now() });
  return data;
}


// ===== Delete Portfolio =====
let isProcessingDelete = false;

async function handleDeletePortfolioButtonClick() {
  if (isProcessingDelete) return;
  isProcessingDelete = true;

  // Immediate visual feedback
  const button = document.getElementById('deletePortfolioButton');
  const originalText = button ? button.textContent : null;
  if (button) {
	button.textContent = 'Processing...';
	button.disabled = true;
	button.style.opacity = '0.2';
  }

  try {
	const initiatorPortfolioId = window.traderPortfolioId;
	const sessionId = localStorage.getItem('sessionId');

	if (!initiatorPortfolioId) {
	  throw new Error(t('triggers.initiatorUndefined', 'Initiator Portfolio ID is undefined'));
	}
	if (!sessionId) {
	  throw new Error(t('triggers.sessionUndefined', 'Session ID is undefined'));
	}

	// Cached portfolios
	const activePortfolios = await getCachedPortfolios(sessionId, '/api/portfolios/active');
	const eligiblePortfolios = activePortfolios.filter((p) => p._id !== initiatorPortfolioId && !p.isSuperUser);

	if (eligiblePortfolios.length === 0) {
	  showNotification(
		t('triggers.noEligible', 'No eligible portfolios to redistribute. Action cancelled.'),
		false
	  );
	  return;
	}

	const selectedUser = await showUserSelectionPopup(eligiblePortfolios);
	if (!selectedUser || !selectedUser.selectedUserId) return;

	const portfolioToDeleteId = selectedUser.selectedUserId;

	const confirmDelete = await showConfirmDeletePopup();
	if (!confirmDelete) return;

	const deleteResponse = await fetch(`${apiBaseUrl}/api/button/useDeletePortfolioButton`, {
	  method: 'POST',
	  headers: {
		'Authorization': `Bearer ${getAuthToken()}`,
		'Content-Type': 'application/json',
	  },
	  body: JSON.stringify({
		initiatorPortfolioId,
		portfolioToDeleteId,
		sessionId,
	  }),
	});

	if (!deleteResponse.ok) {
	  const errorData = await deleteResponse.json().catch(() => ({}));
	  throw new Error(errorData.message || 'Failed to redistribute portfolio');
	}

	// Parse the response to get redistribution details
	const deleteResult = await deleteResponse.json();

	await waitForSocketConnection();
	socket.emit('message', {
	  action: 'deletePortfolioPerformed',
	  initiatorPortfolioId,
	  targetPortfolioId: portfolioToDeleteId,
	  sessionId,
	  redistributionDetails: deleteResult.redistributionDetails,
	  targetPortfolioName: deleteResult.targetPortfolioName,
	  initiatorPortfolioName: deleteResult.initiatorPortfolioName
	});

	showNotification(
	  t('triggers.success', "The selected portfolio's holdings have been successfully redistributed."),
	  true,
	  () => location.reload(),
	);
  } catch (error) {
	console.error('Error using delete portfolio button:', error);
	showNotification(
	  `${t('triggers.error', 'Error: Could not redistribute the selected portfolio.')} ${error.message}`,
	  false
	);
  } finally {
	if (button) {
	  button.textContent = originalText;
	  button.disabled = false;
	  button.style.opacity = '1';
	}
	isProcessingDelete = false;
  }
}


function showConfirmDeletePopup() {
  return new Promise((resolve) => {
	const popup = document.createElement('div');
	popup.id = 'multiUserSelectionPopup';

	// build DOM nodes instead of full innerHTML
	const title = document.createElement('h2');
	title.textContent = t('triggers.title1', "Are you sure you want to redistribute the selected portfolio's holdings?");
	const body = document.createElement('p');
	body.textContent = t('triggers.body1', "This will randomly redistribute the trader's portfolio holdings to random traders in the market.");

	const buttonContainer = document.createElement('div');
	buttonContainer.className = 'selButMuCon';
	
	const confirmButton = document.createElement('button');
	confirmButton.className = 'cyber-confirm-btn'; // Add this line
	confirmButton.textContent = t('triggers.cta1', 'Yes, Trigger Communist Assault');
	Object.assign(confirmButton.style, { padding: '10px 20px', cursor: 'pointer' });
	
	const cancelButton = document.createElement('button');
	cancelButton.className = 'cyber-cancel-btn'; // Add this line
	cancelButton.textContent = t('triggers.cancel', 'Cancel');
	Object.assign(cancelButton.style, { padding: '10px 20px', cursor: 'pointer' });
	
	buttonContainer.append(confirmButton, cancelButton);
	popup.append(title, body, buttonContainer);
	document.body.appendChild(popup);
	
	confirmButton.addEventListener('click', () => {
	  popup.remove();
	  resolve(true);
	}, { once: true });
	
	cancelButton.addEventListener('click', () => {
	  popup.remove();
	  resolve(false);
	}, { once: true });
  });
}


// --- showUserSelectionPopup
	function showUserSelectionPopup(users) {
	  return new Promise((resolve) => {
		const popup = document.createElement('div');
		popup.id = 'multiUserSelectionPopup';

		const title = document.createElement('h2');
		title.textContent = t('triggers.selectPortfolioTitle', 'Select a Portfolio:');

		const select = document.createElement('select');
		select.id = 'userSelectMU';
		users.forEach(user => {
		  const option = document.createElement('option');
		  option.value = user._id;
		  const name = user.name || user.username || user.displayName || t('triggers.unknownUser', 'Unknown user');
		  const wealth = Number(user.totalWealth ?? 0).toFixed(2);
		  option.textContent = `${name} - ${t('triggers.statsTotalWealth','Total Wealth:')} ${wealth}`;
		  select.appendChild(option);
		});
	
		const buttonContainer = document.createElement('div');
		buttonContainer.className = 'selButMuCon';
		
		const confirmButton = document.createElement('button');
		confirmButton.id = 'confirmSelectionMU';
		confirmButton.className = 'cyber-confirm-btn'; // Add this line
		confirmButton.textContent = t('triggers.confirm', 'Confirm');
		
		const cancelButton = document.createElement('button');
		cancelButton.id = 'cancelSelectionMU';
		cancelButton.className = 'cyber-cancel-btn'; // Add this line
		cancelButton.textContent = t('triggers.cancel', 'Cancel');
		
		buttonContainer.append(confirmButton, cancelButton);
		popup.append(title, select, buttonContainer);
		document.body.appendChild(popup);
		
		confirmButton.addEventListener('click', () => {
		  const selectedUserId = select.value;
		  popup.remove();
		  resolve({ selectedUserId });
		}, { once: true });
		
		cancelButton.addEventListener('click', () => {
		  popup.remove();
		  resolve(null);
		}, { once: true });
	  });
	}




function displayDeletePortfolioUpdate(data) {
	  const message = data.message || 'No message provided.';
	  const type = data.type || 'unknown'; // 'victim' or 'recipient'
	  console.log('Displaying Communist Assault notification:', { type, data });

	  const notificationPopup = document.createElement('div');
	  notificationPopup.className = 'subsidy-popup';

	  // Create main message
	  const messageDiv = document.createElement('div');
	  messageDiv.innerHTML = `<strong>${message}</strong>`;
	  notificationPopup.appendChild(messageDiv);

	  // If this user received assets, show details
	  if (type === 'recipient') {
		const detailsDiv = document.createElement('div');
		detailsDiv.style.marginTop = '15px';
		detailsDiv.style.textAlign = 'left';

		const detailsList = [];

		// Show received items
		if (data.receivedItems && data.receivedItems.length > 0) {
		  detailsList.push(`<strong>Items:</strong> ${data.receivedItems.map(item => item.itemName).join(', ')}`);
		}

		// Show received stocks
		if (data.receivedStocks && data.receivedStocks.length > 0) {
		  const stocksList = data.receivedStocks.map(stock => `${stock.stockName} (${stock.quantity} shares)`).join(', ');
		  detailsList.push(`<strong>Stocks:</strong> ${stocksList}`);
		}

		// Show received zollars
		if (data.receivedZollars && data.receivedZollars > 0) {
		  detailsList.push(`<strong>Zollars:</strong> ${data.receivedZollars}`);
		}

		// Show received points
		if (data.receivedPoints && data.receivedPoints > 0) {
		  detailsList.push(`<strong>Trading Capital:</strong> ${data.receivedPoints}`);
		}

		if (detailsList.length > 0) {
		  detailsDiv.innerHTML = '<div style="margin-top: 10px; margin-bottom: 10px;"><strong>You received:</strong></div>' + detailsList.join('<br>');
		  notificationPopup.appendChild(detailsDiv);
		}
	  }

	  // If this user was attacked, show attacker info and what was removed
	  if (type === 'victim') {
		const attackerDiv = document.createElement('div');
		attackerDiv.style.marginTop = '10px';
		const attackerName = data.botName || data.initiatorName || 'Unknown';
		attackerDiv.innerHTML = `<em>Attacked by: ${attackerName}</em>`;
		notificationPopup.appendChild(attackerDiv);

		// Show what was removed from the victim
		const removedDiv = document.createElement('div');
		removedDiv.style.marginTop = '15px';
		removedDiv.style.textAlign = 'left';
		removedDiv.style.color = '#ff6b6b';

		const removedList = [];

		if (data.removedItems && data.removedItems > 0) {
		  removedList.push(`<strong>Commodities Lost:</strong> ${data.removedItems}`);
		}

		if (data.removedStocks && data.removedStocks > 0) {
		  removedList.push(`<strong>Stocks Lost:</strong> ${data.removedStocks} holdings`);
		}

		if (data.removedZollars && data.removedZollars > 0) {
		  removedList.push(`<strong>Zollars Lost:</strong> ${data.removedZollars}`);
		}

		if (data.removedPoints && data.removedPoints > 0) {
		  removedList.push(`<strong>Trading Capital Lost:</strong> ${data.removedPoints}`);
		}

		if (removedList.length > 0) {
		  removedDiv.innerHTML = '<div style="margin-top: 10px; margin-bottom: 10px;"><strong>Assets Redistributed:</strong></div>' + removedList.join('<br>');
		  notificationPopup.appendChild(removedDiv);
		}
	  }

	  const closeButton = document.createElement('button');
	  closeButton.textContent = t('triggers.close', 'Close');
	  closeButton.className = 'close-button-subsidy';
	  closeButton.style.marginTop = '15px';

	  closeButton.addEventListener('click', () => {
		document.body.removeChild(notificationPopup);
	  });

	  notificationPopup.appendChild(closeButton);
	  document.body.appendChild(notificationPopup);
	}

function initializeLBOButton() {
  const LBOButton = document.getElementById('LBOButton');
  if (LBOButton) {
	LBOButton.removeEventListener('click', handleLBOButtonClick);
	LBOButton.addEventListener('click', handleLBOButtonClick);
  }
}


// ===== LBO =====
let isProcessingLBO = false;

async function handleLBOButtonClick() {
  if (isProcessingLBO) return; // Prevent double-clicks
  isProcessingLBO = true;

  // Immediate visual feedback
  const button = document.getElementById('LBOButton');
  const originalText = button ? button.textContent : null;
  if (button) {
	button.textContent = 'Processing...';
	button.disabled = true;
	button.style.opacity = '0.2';
  }

  // Remove any existing popups
  const existingPopups = document.querySelectorAll('.lbo-popup, #multiUserSelectionPopup');
  existingPopups.forEach(popup => popup.remove());

  try {
	const initiatorPortfolioId = window.traderPortfolioId;
	const sessionId = localStorage.getItem('sessionId');

	if (!initiatorPortfolioId) throw new Error(t('triggers.initiatorUndefined', 'Initiator Portfolio is undefined'));
	if (!sessionId) throw new Error(t('triggers.sessionUndefined', 'Session ID is undefined'));

	// Cached active portfolios
	const activePortfolios = await getCachedPortfolios(sessionId, '/api/portfolios/active');
	const eligiblePortfolios = activePortfolios.filter(p => p._id !== initiatorPortfolioId && !p.isSuperUser);

	if (eligiblePortfolios.length === 0) {
	  showNotification(
		t('triggers.noEligible', 'No eligible portfolios to redistribute. Action cancelled.'),
		false
	  );
	  return;
	}

	// Show target selection popup
	const selectedTarget = await showUserSelectionPopupLBO(eligiblePortfolios);
	if (!selectedTarget || !selectedTarget.selectedUserId) return;

	const targetPortfolioId = selectedTarget.selectedUserId;

	// Fetch target's holdings
	const targetPortfolioResponse = await fetch(`${apiBaseUrl}/api/portfolios/${targetPortfolioId}`, {
	  method: 'GET',
	  headers: { 'Authorization': `Bearer ${getAuthToken()}` }
	});
	if (!targetPortfolioResponse.ok) {
	  throw new Error(`Failed to fetch target portfolio: ${targetPortfolioResponse.status}`);
	}
	const targetPortfolioData = await targetPortfolioResponse.json();

	// Popup for selecting stocks/items
	const { selectedItems = [], selectedStocks = [] } = await showLBOSelectionPopup(targetPortfolioData) || {};

	if (!selectedItems.length && !selectedStocks.length) {
	  showNotification(t('triggers.noAssetsSelected', 'No assets selected for LBO.'), false);
	  return;
	}

	const LBOCost = calculateLBOCost(selectedItems, selectedStocks);

	const confirmLBO = await showConfirmLBOPopup(LBOCost);
	if (!confirmLBO) return;

	// Send LBO request
	const lboResponse = await fetch(`${apiBaseUrl}/api/button/useLBOButton`, {
	  method: 'POST',
	  headers: {
		'Authorization': `Bearer ${getAuthToken()}`,
		'Content-Type': 'application/json'
	  },
	  body: JSON.stringify({
		initiatorPortfolioId,
		targetPortfolioId,
		sessionId,
		selectedItems,
		selectedStocks,
		LBOCost
	  })
	});

	if (!lboResponse.ok) {
	  const errorData = await lboResponse.json().catch(() => ({}));
	  throw new Error(errorData.message || 'Failed to perform LBO');
	}

	const { addedStocks, removedStocks, addedItems, removedItems } = await lboResponse.json();

	await waitForSocketConnection();
	socket.emit('message', {
	  action: 'LBOPerformed',
	  initiatorPortfolioId,
	  targetPortfolioId,
	  removedStocks,
	  removedItems
	});

	showNotification(
	  t('triggers.successLBO', 'LBO triggered successfully.'),
	  true,
	  () => location.reload()
	);
  } catch (error) {
	console.error('Error using LBO button:', error);
	showNotification(`${t('triggers.errorPrefix', 'Error:')} ${error.message}`, false);
  } finally {
	if (button) {
	  button.textContent = originalText;
	  button.disabled = false;
	  button.style.opacity = '1';
	}
	isProcessingLBO = false;
  }
}



function showLBOSelectionPopup(targetPortfolioData) {
	  return new Promise((resolve) => {
		const popup = document.createElement('div');
		popup.className = 'lbo-popup';
	
		const itemOptions = targetPortfolioData.items?.map(item => `
		  <label>
			<input type="checkbox" data-id="${item._id}" data-points="${item.points || 0}">
			${item.item_name} - ${item.points || 0} ${t('triggers.pointsLabel', 'Capital')}
		  </label>
		`).join('') || `<p>${t('triggers.noItems', 'No commodities available')}</p>`;
	
		const stockOptions = targetPortfolioData.stocks?.map(stock => `
		  <label>
			<input type="checkbox" data-id="${stock.stock._id}" data-value="${stock.stock.shareValue}" data-quantity="${stock.quantity}">
			${t(`companies_by_id.${String(stock.stock.csvId || stock.stock.companyCsvId || stock.stock.id || stock.stock._id || '')}.Company_Name`, stock.stock.name)} - ${stock.quantity} ${t('triggers.sharesAt', 'shares at {value} each').replace('{value}', (stock.stock.shareValue || 0))}
		  </label>
		`).join('') || `<p>${t('triggers.noStocks', 'No stocks available')}</p>`;
	
		popup.innerHTML = `
		  <h2>${t('triggers.selectAssetsTitle', 'Select assets for the LBO')}</h2>
		  <h3>${t('triggers.selectAssetsItems','Commodities:')}</h3>
		  ${itemOptions}
		  <h3>${t('triggers.selectAssetsStocks','Stocks:')}</h3>
		  ${stockOptions}
		  <div class="lbo-buttons">
			<button id="confirmSelectionLBO" class="lbo-button">${t('triggers.confirm', 'Confirm')}</button>
			<button id="cancelSelectionLBO" class="lbo-button">${t('triggers.cancel', 'Cancel')}</button>
		  </div>
		`;
	
		document.body.appendChild(popup);
	
		const confirmButton = document.getElementById('confirmSelectionLBO');
		const cancelButton = document.getElementById('cancelSelectionLBO');
	
		confirmButton.addEventListener('click', () => {
		  const selectedItems = Array.from(popup.querySelectorAll('input[type="checkbox"]:checked[data-points]'))
			.map(input => ({
			  id: input.getAttribute('data-id'),
			  points: parseInt(input.getAttribute('data-points'), 10)
			}));
	
		  const selectedStocks = Array.from(popup.querySelectorAll('input[type="checkbox"]:checked[data-quantity]'))
			.map(input => ({
			  id: input.getAttribute('data-id'),
			  quantity: parseInt(input.getAttribute('data-quantity'), 10),
			  shareValue: parseFloat(input.getAttribute('data-value'))
			}));
	
		  document.body.removeChild(popup);
		  resolve({ selectedItems, selectedStocks });
		});
	
		cancelButton.addEventListener('click', () => {
		  document.body.removeChild(popup);
		  resolve({ selectedItems: [], selectedStocks: [] });
		});
	  });
	}


function calculateLBOCost(selectedItems = [], selectedStocks = []) {
  let cost = 0;

  selectedItems.forEach(item => {
	cost += item.points || 0; // Sum the points for each selected item
  });

  selectedStocks.forEach(stock => {
	cost += (stock.shareValue || 0) * (stock.quantity || 0); // Calculate the total value for each selected stock
  });

  return Math.ceil(cost * 1.5); // Return 150% of the selected asset cost
}



function showUserSelectionPopupLBO(users) {
  return new Promise((resolve) => {
	const popup = document.createElement('div');
	popup.id = 'multiUserSelectionPopup';

	const title = document.createElement('h2');
	title.textContent = t('triggers.title_1', 'Select a portfolio to trigger an LBO on:');

	const select = document.createElement('select');
	select.id = 'userSelectLBO';
	users.forEach(user => {
	  const option = document.createElement('option');
	  option.value = user._id;
	  const name = user.name || user.username || user.displayName || t('triggers.unknownUser', 'Unknown user');
	  const wealth = Number(user.totalWealth ?? 0).toFixed(2);
	  option.textContent = `${name} - ${t('triggers.statsTotalWealth','Total Wealth:')} ${wealth}`;
	  select.appendChild(option);
	});

	const buttonContainer = document.createElement('div');
	buttonContainer.className = 'selButMuCon';

	const confirmButton = document.createElement('button');
	confirmButton.className = 'cyber-confirm-btn';
	confirmButton.textContent = t('triggers.confirm', 'Confirm');

	const cancelButton = document.createElement('button');
	cancelButton.className = 'cyber-cancel-btn';
	cancelButton.textContent = t('triggers.cancel', 'Cancel');

	buttonContainer.append(confirmButton, cancelButton);
	popup.append(title, select, buttonContainer);
	document.body.appendChild(popup);

	confirmButton.addEventListener('click', () => {
	  popup.remove();
	  resolve({ selectedUserId: select.value });
	}, { once: true });

	cancelButton.addEventListener('click', () => {
	  popup.remove();
	  resolve(null);
	}, { once: true });
  });
}


function showConfirmLBOPopup(LBOCost) {
  return new Promise((resolve) => {
	const popup = document.createElement('div');
	popup.id = 'multiUserSelectionPopup';

	const title = document.createElement('h2');
	title.textContent = t('triggers.titleLBO', 'Confirm Leveraged Buyout');

	const body = document.createElement('p');
	body.textContent = t(
	  'triggers.bodyLBO',
	  'This LBO will cost you {cost} of capital. Do you wish to proceed?'
	).replace('{cost}', LBOCost);

	const buttonContainer = document.createElement('div');
	buttonContainer.className = 'selButMuCon';

	const confirmButton = document.createElement('button');
	confirmButton.className = 'cyber-confirm-btn'; // Add this line
	confirmButton.textContent = t('triggers.ctaLBO', 'Yes, trigger the LBO');
	Object.assign(confirmButton.style, { padding: '10px 20px', cursor: 'pointer' });

	const cancelButton = document.createElement('button');
	cancelButton.className = 'cyber-cancel-btn'; // Add this line
	cancelButton.textContent = t('triggers.cancel', 'Cancel');
	Object.assign(cancelButton.style, { padding: '10px 20px', cursor: 'pointer' });

	buttonContainer.append(confirmButton, cancelButton);
	popup.append(title, body, buttonContainer);
	document.body.appendChild(popup);

	confirmButton.addEventListener('click', () => {
	  popup.remove();
	  resolve(true);
	}, { once: true });

	cancelButton.addEventListener('click', () => {
	  popup.remove();
	  resolve(false);
	}, { once: true });
  });
}


function handleLBOUpdateForTarget(data) {
  const { message, removedStocks = [], removedItems = [] } = data;

  const notificationPopup = document.createElement('div');
  notificationPopup.className = 'lbo-popup';

  let details = `<div>${message}</div><ul>`;

  if (removedStocks.length > 0) {
	details += `<li><strong>${t('triggers.removedStocksLabel', 'Removed Stocks:')}</strong> `;
details += removedStocks.map(stock =>
	  `${stock.quantity} ${t('triggers.sharesWord', 'shares')} ${t('triggers.ofWord', 'of')} ${t('companies_by_id.' + String(stock.csvId || stock.companyCsvId || stock.id || stock.stockId || stock._id || '') + '.Company_Name', stock.name || t('triggers.unknownStock', 'Unknown Stock'))}`
	).join(', ');
	details += `</li>`;
  }

  if (removedItems.length > 0) {
	details += `<li><strong>${t('triggers.removedItemsLabel', 'Removed Commodities:')}</strong> `;
	details += removedItems.map(item => item.name || t('triggers.unknownItem', 'Unknown Commodity')).join(', ');
	details += `</li>`;
  }

  details += `</ul>`;
  notificationPopup.innerHTML = details;

  // Use a real button element
  const closeButton = document.createElement('button');
  closeButton.textContent = t('triggers.close', 'Close');
  closeButton.className = 'close-button-LBO';
  closeButton.style.marginTop = '10px';

  closeButton.addEventListener('click', () => {
	document.body.removeChild(notificationPopup);
  });

  notificationPopup.appendChild(closeButton);
  document.body.appendChild(notificationPopup);
}


function initializeSubsidyButton() {
  const SubsidyButton = document.getElementById('SubsidyButton');
  if (SubsidyButton) {
	SubsidyButton.removeEventListener('click', handleSubsidyButtonClick);
	SubsidyButton.addEventListener('click', handleSubsidyButtonClick);
  }
}

// ===== Subsidy =====
let isProcessingSubsidy = false;

async function handleSubsidyButtonClick() {
  if (isProcessingSubsidy) return;
  isProcessingSubsidy = true;

  // Immediate visual feedback
  const button = document.getElementById('SubsidyButton');
  const originalText = button ? button.textContent : null;
  if (button) {
	button.textContent = 'Processing...';
	button.disabled = true;
	button.style.opacity = '0.2';
  }

  try {
	const initiatorPortfolioId = window.traderPortfolioId;
	const sessionId = localStorage.getItem('sessionId');

	if (!initiatorPortfolioId) throw new Error(t('triggers.initiatorUndefined', 'Initiator Portfolio is undefined'));
	if (!sessionId) throw new Error(t('triggers.sessionUndefined', 'Session is undefined'));

	// Cached richest portfolios
	const richPortfolios = await getCachedPortfolios(sessionId, '/api/portfolios/richest');

	if (!richPortfolios || richPortfolios.length === 0) {
	  showNotification(t('triggers.noRichPortfolios', 'No rich portfolios available to target.'), false);
	  return;
	}

	const selectedTarget = await showUserSelectionPopupSubsidy(richPortfolios);
	if (!selectedTarget || !selectedTarget.selectedUserId) return;

	const targetPortfolioId = selectedTarget.selectedUserId;

	const subsidyResponse = await fetch(`${apiBaseUrl}/api/button/useSubsidyButton`, {
	  method: 'POST',
	  headers: {
		'Authorization': `Bearer ${getAuthToken()}`,
		'Content-Type': 'application/json'
	  },
	  body: JSON.stringify({
		initiatorPortfolioId,
		targetPortfolioId,
		sessionId
	  })
	});

	if (!subsidyResponse.ok) {
	  const errorData = await subsidyResponse.json().catch(() => ({}));
	  throw new Error(errorData.message || 'Failed to perform Subsidy action');
	}

	// Extract the detailed amounts from the API response
	const subsidyData = await subsidyResponse.json();

	await waitForSocketConnection();
	socket.emit('message', {
	  action: 'SubsidyPerformed',
	  initiatorPortfolioId,
	  targetPortfolioId,
	  sessionId,
	  taxedAmount: subsidyData.taxedAmount || 0,
	  taxedZollars: subsidyData.taxedZollars || 0,
	  taxedItems: subsidyData.taxedItems || 0
	});

	// NOTE: your flat key is "success3" (not namespaced)
	showNotification(t('triggers.success3', 'Subsidy action triggered successfully.'), true, () => location.reload());
  } catch (error) {
	console.error('Error using Subsidy button:', error);
	showNotification(`${t('triggers.errorPrefix', 'Error:')} ${error.message}`, false);
  } finally {
	if (button) {
	  button.textContent = originalText;
	  button.disabled = false;
	  button.style.opacity = '1';
	}
	isProcessingSubsidy = false;
  }
}


function showUserSelectionPopupSubsidy(users) {
  return new Promise((resolve) => {
	const popup = document.createElement('div');
	popup.id = 'multiUserSelectionPopup';

	const title = document.createElement('h2');
	title.textContent = t('triggers.selectRichTitle3', 'Select a rich portfolio to tax:');

	const select = document.createElement('select');
	select.id = 'userSelectSubsidy';
	users.forEach(user => {
	  const option = document.createElement('option');
	  option.value = user._id;
	  option.textContent = `${user.name} - ${t('triggers.statsTotalWealth', 'Total Wealth:')} ${user.totalWealth.toFixed(2)}`;
	  select.appendChild(option);
	});

	const buttonContainer = document.createElement('div');
	buttonContainer.className = 'selButMuCon';

	const confirmButton = document.createElement('button');
	confirmButton.className = 'cyber-confirm-btn'; // Add this line
	confirmButton.textContent = t('triggers.confirm', 'Confirm');

	const cancelButton = document.createElement('button');
	cancelButton.className = 'cyber-cancel-btn'; // Add this line
	cancelButton.textContent = t('triggers.cancel', 'Cancel');

	buttonContainer.append(confirmButton, cancelButton);
	popup.append(title, select, buttonContainer);
	document.body.appendChild(popup);

	confirmButton.addEventListener('click', () => {
	  popup.remove();
	  resolve({ selectedUserId: select.value });
	}, { once: true });

	cancelButton.addEventListener('click', () => {
	  popup.remove();
	  resolve(null);
	}, { once: true });
  });
}



function handleSubsidyUpdateForTarget(data) {
  let { message, initiatorName, taxedAmount, taxedZollars, taxedItems } = data;

  // Build detailed notification message
  let detailsHTML = '';
  if (initiatorName) {
	detailsHTML += `<p style="font-weight: bold; color: #fcba03; margin-bottom: 10px;">${initiatorName} triggered a Socialist Subsidy against you!</p>`;
  }

  detailsHTML += `<p style="margin-bottom: 15px;">45% of your wealth has been redistributed to lower income earners:</p>`;

  if (taxedAmount > 0 || taxedZollars > 0 || taxedItems > 0) {
	detailsHTML += `<ul style="list-style: none; padding: 0; margin: 10px 0;">`;
	if (taxedAmount > 0) {
	  detailsHTML += `<li>💸 Capital: <span style="color: #ff4444;">${taxedAmount.toFixed(2)}</span></li>`;
	}
	if (taxedZollars > 0) {
	  detailsHTML += `<li>Ƶ Zollars: <span style="color: #ff4444;">${taxedZollars.toFixed(2)}</span></li>`;
	}
	if (taxedItems > 0) {
	  detailsHTML += `<li>📦 Items: <span style="color: #ff4444;">${taxedItems}</span></li>`;
	}
	detailsHTML += `</ul>`;
  }

  const notificationPopup = document.createElement('div');
  notificationPopup.className = 'subsidy-popup';

  notificationPopup.innerHTML = detailsHTML;

  const closeButton = document.createElement('button');
  closeButton.textContent = 'Close';
  closeButton.className = 'close-button-subsidy';
  closeButton.style.marginTop = '10px';

  closeButton.addEventListener('click', () => {
	document.body.removeChild(notificationPopup);
  });

  notificationPopup.appendChild(closeButton);
  document.body.appendChild(notificationPopup);
}

function handleSubsidyReceived(data) {
  let { message, initiatorName, targetName, receivedAmount, receivedZollars } = data;

  // Build detailed notification message
  let detailsHTML = '<div style="background: linear-gradient(135deg, rgba(61, 210, 2, 0.15), rgba(61, 210, 2, 0.05)); padding: 15px; border-radius: 8px; margin-bottom: 15px;">';
  detailsHTML += '<p style="font-weight: bold; color: #3dd202; margin-bottom: 10px; font-size: 1.1em;">🎁 Socialist Subsidy Received!</p>';

  if (initiatorName && targetName) {
	detailsHTML += `<p style="margin-bottom: 15px; color: #ddd;"><strong style="color: #3dd202;">${initiatorName}</strong> redistributed wealth from <strong style="color: #fcba03;">${targetName}</strong></p>`;
  } else if (initiatorName) {
	detailsHTML += `<p style="margin-bottom: 15px; color: #ddd;"><strong style="color: #3dd202;">${initiatorName}</strong> triggered a wealth redistribution</p>`;
  } else {
	detailsHTML += `<p style="margin-bottom: 15px; color: #ddd;">You received a socialist subsidy</p>`;
  }
  detailsHTML += '</div>';

  if (receivedAmount > 0 || receivedZollars > 0) {
	detailsHTML += `<div style="background: rgba(0,0,0,0.3); padding: 15px; border-radius: 8px; margin-bottom: 10px;">`;
	detailsHTML += `<div style="font-weight: bold; margin-bottom: 10px; color: #fff;">📈 You Received:</div>`;
	detailsHTML += `<ul style="list-style: none; padding: 0; margin: 0;">`;
	if (receivedAmount > 0) {
	  detailsHTML += `<li style="padding: 8px; margin: 5px 0; background: rgba(61, 210, 2, 0.1); border-left: 3px solid #3dd202; border-radius: 4px;">💰 Capital: <span style="color: #3dd202; font-weight: bold; font-size: 1.1em;">+${receivedAmount.toFixed(2)}</span></li>`;
	}
	if (receivedZollars > 0) {
	  detailsHTML += `<li style="padding: 8px; margin: 5px 0; background: rgba(61, 210, 2, 0.1); border-left: 3px solid #3dd202; border-radius: 4px;">Ƶ Zollars: <span style="color: #3dd202; font-weight: bold; font-size: 1.1em;">+${receivedZollars.toFixed(2)}</span></li>`;
	}
	detailsHTML += `</ul></div>`;
  } else {
	detailsHTML += `<p style="padding: 10px; background: rgba(61, 210, 2, 0.1); border-radius: 5px; color: #ddd;">You have received assistance from the wealth redistribution program.</p>`;
  }

  detailsHTML += `<div style="margin-top: 15px; padding-top: 15px; border-top: 1px solid #444; font-size: 0.85em; color: #888; font-style: italic;">
	💡 This wealth came from economic redistribution. Someone helped balance the economy!
  </div>`;

  const notificationPopup = document.createElement('div');
  notificationPopup.className = 'subsidy-popup';

  notificationPopup.innerHTML = detailsHTML;

  const closeButton = document.createElement('button');
  closeButton.textContent = 'Close';
  closeButton.className = 'close-button-subsidy';
  closeButton.style.marginTop = '10px';

  closeButton.addEventListener('click', () => {
	document.body.removeChild(notificationPopup);
  });

  notificationPopup.appendChild(closeButton);

  document.body.appendChild(notificationPopup);
}

function showUserSelectionPopupRS(users) {
  return new Promise((resolve) => {
	const popup = document.createElement('div');
	popup.id = 'multiUserSelectionPopup';

	const title = document.createElement('h2');
	title.textContent = t('triggers.ransomSelectTargetTitle', 'Select a portfolio to ransom:');

	const select = document.createElement('select');
	select.id = 'userSelectSubsidy';
	users.forEach(user => {
	  const option = document.createElement('option');
	  option.value = user._id;
	  const name = user.name || user.username || user.displayName || t('triggers.unknownUser', 'Unknown user');
	  const wealth = Number(user.totalWealth ?? 0).toFixed(2);
	  option.textContent = `${name} - ${t('triggers.statsTotalWealth','Total Wealth:')} ${wealth}`;
	  select.appendChild(option);
	});

	const buttonContainer = document.createElement('div');
	buttonContainer.className = 'selButMuCon';

	const confirmButton = document.createElement('button');
	confirmButton.className = 'cyber-confirm-btn';
	confirmButton.textContent = t('triggers.confirm', 'Confirm');

	const cancelButton = document.createElement('button');
	cancelButton.className = 'cyber-cancel-btn';
	cancelButton.textContent = t('triggers.cancel', 'Cancel');

	buttonContainer.append(confirmButton, cancelButton);
	popup.append(title, select, buttonContainer);
	document.body.appendChild(popup);

	confirmButton.addEventListener('click', () => {
	  const selectedUserId = select.value;
	  popup.remove();
	  resolve(selectedUserId);
	}, { once: true });

	cancelButton.addEventListener('click', () => {
	  popup.remove();
	  resolve(null);
	}, { once: true });
  });
}

function initializeStealButton() {
  const stealButton = document.getElementById('stealButton');
  if (stealButton) {
	stealButton.addEventListener('click', handleStealButtonClick);
  }
}

// ===== Steal =====
let isProcessingSteal = false;

async function handleStealButtonClick() {
  if (isProcessingSteal) return;
  isProcessingSteal = true;

  // Immediate visual feedback
  const button = document.getElementById('stealButton');
  const originalText = button ? button.textContent : null;
  if (button) {
	button.textContent = 'Processing...';
	button.disabled = true;
	button.style.opacity = '0.2';
  }

  try {
	const portfolioId = window.traderPortfolioId;
	console.log('Using steal button for portfolio:', portfolioId);

	if (!portfolioId) {
	  throw new Error(t('triggers.initiatorUndefined', 'Portfolio is undefined'));
	}

	const useButtonResponse = await fetch(`${apiBaseUrl}/api/button/useStealButton`, {
	  method: 'POST',
	  headers: {
		'Content-Type': 'application/json',
		'Authorization': `Bearer ${getAuthToken()}`
	  },
	  body: JSON.stringify({ portfolioId })
	});

	console.log('Use button response status:', useButtonResponse.status);

	if (!useButtonResponse.ok) {
	  const errorText = await useButtonResponse.text();
	  console.error('Error response body:', errorText);
	  throw new Error(`${t('triggers.stealFailedUse', 'Failed to use steal button')}: ${useButtonResponse.status} - ${errorText}`);
	}

	const useButtonResult = await useButtonResponse.json();
	console.log(useButtonResult.message);

	// Fetch available items
	const itemsResponse = await fetch(`${apiBaseUrl}/api/button/availableItems`, {
	  headers: { 'Authorization': `Bearer ${getAuthToken()}` }
	});
	console.log('Items response status:', itemsResponse.status);

	if (!itemsResponse.ok) {
	  const errorText = await itemsResponse.text();
	  console.error('Error response body:', errorText);
	  throw new Error(`${t('triggers.stealFailedFetchItems', 'Failed to fetch available commodities')}: ${itemsResponse.status} - ${errorText}`);
	}

	const items = await itemsResponse.json();
	console.log('Available items:', items);

	displayAvailableItemsST(items);
  } catch (error) {
	console.error('Error using steal button:', error);
	showNotification(`${t('triggers.stealFailedUseOrFetch', 'Failed to use steal button or fetch commmodities')}: ${error.message}`, false);
  } finally {
	if (button) {
	  button.textContent = originalText;
	  button.disabled = false;
	  button.style.opacity = '1';
	}
	isProcessingSteal = false;
  }
}


function displayAvailableItemsST(items) {
	  console.log('Displaying available items:', items);
	
	  const existingModal = document.querySelector('.modal-steal');
	  if (existingModal) {
		existingModal.remove();
	  }
	
	  const modal = document.createElement('div');
	  modal.classList.add('modal-steal');
	
	  const modalContent = document.createElement('div');
	  modalContent.classList.add('modal-steal-content');
	
	  const closeBtn = document.createElement('span');
	  closeBtn.classList.add('modal-steal-close');
	  closeBtn.textContent = 'X';
	  closeBtn.onclick = () => document.body.removeChild(modal);
	
	  const itemList = document.createElement('ul');
	  itemList.classList.add('modal-steal-item-list'); // Apply the scrollable list class
	  items.forEach(item => {
		const listItem = document.createElement('li');
		listItem.textContent = `${item.item_name} - ${item.description}`;
		listItem.style = `
		  cursor: pointer; padding: 5px; border-bottom: 1px solid #00ff00;
		`;
		listItem.onclick = () => {
		  stealItem(item._id);
		  document.body.removeChild(modal);
		};
		itemList.appendChild(listItem);
	  });
	
	  modalContent.appendChild(closeBtn);
	  modalContent.appendChild(itemList);
	  modal.appendChild(modalContent);
	  document.body.appendChild(modal);
	
	  console.log('Steal modal created and added to the DOM');
	}


async function stealItem(itemId) {
	  try {
		const portfolioId = window.traderPortfolioId;
		console.log('Attempting to steal item:', itemId, 'for portfolio:', portfolioId);
	
		if (!portfolioId) {
		  throw new Error(t('triggers.initiatorUndefined', 'Portfolio ID is undefined'));
		}
	
		const response = await fetch(`${apiBaseUrl}/api/button/stealItem`, {
		  method: 'POST',
		  headers: {
			'Content-Type': 'application/json',
			'Authorization': `Bearer ${getAuthToken()}`
		  },
		  body: JSON.stringify({ stealerPortfolioId: portfolioId, itemId })
		});
	
		console.log('Steal item response status:', response.status);
	
		// Read body once; we may need it for both success & error
		let payload = null;
		let rawText = '';
		try {
		  rawText = await response.text();
		  payload = rawText ? JSON.parse(rawText) : null;
		} catch {
		  payload = null; // keep going; we’ll fall back to client-side keys
		}
	
		if (!response.ok) {
		  console.error('Error response:', payload || rawText);
	
		  // Prefer backend code if present, else map HTTP status to a client key
		  const statusToKey = {
			403: 'triggers.steal_noTrigger',
			404: 'triggers.steal_itemUnavailable',
			500: 'triggers.steal_serverError'
		  };
	
		  const key = (payload && payload.messageId) || statusToKey[response.status] || 'trigger.steal_serverError';
		  const msg = t(
			key,
			(payload && payload.message) || t('socketFront.stealFailed', 'Failed to steal asset'),
			(payload && payload.params) || {}
		  );
	
		  throw new Error(msg);
		}
	
		// Success: always use the client translation key (don’t trust server strings)
		console.log('Steal item result:', payload);
		const successMsg = t('triggers.steal_success', 'Item stolen successfully.');
		showNotification(successMsg, true, () => location.reload());
	
	  } catch (error) {
		console.error('Error stealing item:', error);
		// Final client-side fallback, still localized
		showNotification(
		  `${t('triggers.stealFailedToSteal', 'Failed to steal asset')}: ${error?.message || ''}`,
		  false
		);
	  }
	}


// ===== Ransom =====
	let isProcessingRansom = false;
	
	async function handleRansomButtonClick() {
	  if (isProcessingRansom) return;
	  isProcessingRansom = true;
	
	  // Immediate visual feedback
	  const button = document.getElementById('ransomButton');
	  const originalText = button ? button.textContent : null;
	  if (button) {
		button.textContent = 'Processing...';
		button.disabled = true;
		button.style.opacity = '0.2';
	  }
	
	  try {
		const portfolioId = window.traderPortfolioId;
		const sessionId = localStorage.getItem('sessionId');
	
		console.log('Initiating ransom action from portfolio:', portfolioId);
	
		if (!portfolioId) throw new Error(t('triggers.initiatorUndefined', 'Initiator Portfolio is undefined'));
		if (!sessionId) throw new Error(t('triggers.sessionUndefined', 'Session is undefined'));
	
		// Cached active portfolios
		const activePortfolios = await getCachedPortfolios(sessionId, '/api/portfolios/active');
		const eligiblePortfolios = activePortfolios.filter(p => p._id !== portfolioId && !p.isSuperUser);
	
		if (eligiblePortfolios.length === 0) {
		  showNotification(t('triggers.ransomNoEligible', 'No eligible portfolios to ransom. Action cancelled.'), false);
		  return;
		}
	
		// May return string or { selectedUserId } depending on previous helper versions
		const sel = await showUserSelectionPopupRS(eligiblePortfolios);
		const portfolioToRansomId = typeof sel === 'string' ? sel : sel?.selectedUserId;
		if (!portfolioToRansomId) return;

		// Find the selected portfolio to get their wealth
		const targetPortfolio = eligiblePortfolios.find(p => p._id === portfolioToRansomId);
		const targetWealth = targetPortfolio?.totalWealth || 0;
		const targetName = targetPortfolio?.name || 'Unknown';

		const ransomAmount = await showRansomAmountPopup(targetWealth, targetName);
		if (!ransomAmount) return;
	
		const confirmRansom = await showConfirmRansomPopup(ransomAmount);
		if (!confirmRansom) return;
	
		const ransomResponse = await fetch(`${apiBaseUrl}/api/button/setRansom`, {
		  method: 'POST',
		  headers: {
			'Content-Type': 'application/json',
			'Authorization': `Bearer ${getAuthToken()}`
		  },
		  body: JSON.stringify({
			portfolioId,
			targetPortfolioId: portfolioToRansomId,
			ransomPoints: ransomAmount
		  })
		});
	
		if (!ransomResponse.ok) {
		  const errorData = await ransomResponse.json().catch(() => ({}));
		  throw new Error(errorData.message || t('triggers.ransomSetFailed', 'Failed to set ransom'));
		}
	
		const result = await ransomResponse.json();
		showNotification(t('triggers.ransomSetSuccess', 'Ransom set successfully.'), true);
	  } catch (error) {
		console.error('Error using ransom button:', error);
		showNotification(`${t('triggers.ransomSetErrorPrefix', 'Error: Could not set ransom.')} ${error.message}`, false);
	  } finally {
		if (button) {
		  button.textContent = originalText;
		  button.disabled = false;
		  button.style.opacity = '1';
		}
		isProcessingRansom = false;
	  }
	}



	
	function showRansomAmountPopup(targetWealth = 0, targetName = 'Unknown') {
	  return new Promise((resolve) => {
		const popup = document.createElement('div');
		popup.id = 'multiUserSelectionPopup';

		const title = document.createElement('h2');
		title.textContent = t('triggers.ransomSetAmountTitle', 'Set Ransom Amount');

		// Display target info
		const targetInfo = document.createElement('p');
		targetInfo.style.cssText = 'margin: 10px 0; color: #0f0; font-size: 1.1em;';
		targetInfo.textContent = `${targetName} - Total Wealth: ${Math.floor(targetWealth)} capital`;

		// Percentage buttons container
		const percentButtonsContainer = document.createElement('div');
		percentButtonsContainer.style.cssText = 'display: flex; gap: 10px; margin: 15px 0; flex-wrap: wrap; justify-content: center;';

		const percentages = [5, 10, 25, 50, 75, 100];
		let selectedAmount = Math.max(1, Math.floor(targetWealth * 0.1)); // Default to 10%, minimum 1

		percentages.forEach(percent => {
		  const btn = document.createElement('button');
		  btn.className = 'cyber-confirm-btn';
		  btn.textContent = `${percent}%`;
		  btn.style.cssText = 'padding: 8px 16px; font-size: 0.9em; min-width: 60px; opacity: 0.6;';
		  btn.type = 'button'; // Prevent form submission

		  btn.addEventListener('click', (e) => {
			e.preventDefault();
			selectedAmount = Math.max(1, Math.floor(targetWealth * (percent / 100)));
			amountDisplay.textContent = `${selectedAmount} capital`;
			slider.value = percent;

			// Visual feedback - highlight selected button
			percentButtonsContainer.querySelectorAll('button').forEach(b => {
			  b.style.opacity = '0.6';
			  b.style.transform = 'scale(1)';
			});
			btn.style.opacity = '1';
			btn.style.transform = 'scale(1.05)';
		  });

		  percentButtonsContainer.appendChild(btn);
		});

		// Slider container
		const sliderContainer = document.createElement('div');
		sliderContainer.style.cssText = 'margin: 20px 0;';

		const sliderLabel = document.createElement('label');
		sliderLabel.textContent = t('triggers.ransomSliderLabel', 'Fine-tune percentage:');
		sliderLabel.style.cssText = 'display: block; margin-bottom: 8px; color: #0ff;';

		const slider = document.createElement('input');
		slider.type = 'range';
		slider.min = '1';
		slider.max = '100';
		slider.value = '10';
		slider.step = '1';
		slider.style.cssText = 'width: 100%; cursor: pointer;';

		const sliderValueDisplay = document.createElement('span');
		sliderValueDisplay.textContent = '10%';
		sliderValueDisplay.style.cssText = 'margin-left: 10px; color: #ff0; font-weight: bold;';

		slider.addEventListener('input', (e) => {
		  const percent = parseInt(e.target.value, 10);
		  selectedAmount = Math.max(1, Math.floor(targetWealth * (percent / 100)));
		  sliderValueDisplay.textContent = `${percent}%`;
		  amountDisplay.textContent = `${selectedAmount} capital`;

		  // Reset button highlights
		  percentButtonsContainer.querySelectorAll('button').forEach(b => {
			b.style.opacity = '0.6';
			b.style.transform = 'scale(1)';
		  });
		});

		sliderContainer.append(sliderLabel, slider, sliderValueDisplay);

		// Amount display
		const amountDisplay = document.createElement('div');
		amountDisplay.style.cssText = 'margin: 20px 0; padding: 15px; background: rgba(0, 255, 0, 0.1); border: 2px solid #0f0; border-radius: 8px; text-align: center; font-size: 1.3em; color: #0f0; font-weight: bold;';
		amountDisplay.textContent = `${selectedAmount} capital`;

		// Confirm/Cancel buttons
		const buttonContainer = document.createElement('div');
		buttonContainer.className = 'selButMuCon';

		const confirmButton = document.createElement('button');
		confirmButton.className = 'cyber-confirm-btn';
		confirmButton.textContent = t('triggers.confirm', 'Confirm');

		const cancelButton = document.createElement('button');
		cancelButton.className = 'cyber-cancel-btn';
		cancelButton.textContent = t('triggers.cancel', 'Cancel');

		buttonContainer.append(confirmButton, cancelButton);
		popup.append(title, targetInfo, percentButtonsContainer, sliderContainer, amountDisplay, buttonContainer);
		document.body.appendChild(popup);

		// Set initial button highlight (10%)
		const tenPercentBtn = percentButtonsContainer.children[1]; // 10% is second button (index 1)
		if (tenPercentBtn) {
		  tenPercentBtn.style.opacity = '1';
		  tenPercentBtn.style.transform = 'scale(1.05)';
		}

		confirmButton.addEventListener('click', () => {
		  popup.remove();
		  resolve(selectedAmount > 0 ? selectedAmount : null);
		}, { once: true });

		cancelButton.addEventListener('click', () => {
		  popup.remove();
		  resolve(null);
		}, { once: true });
	  });
	}


	
function showConfirmRansomPopup(amount) {
	  return new Promise((resolve) => {
		const popup = document.createElement('div');
		popup.id = 'multiUserSelectionPopup';

		const title = document.createElement('h2');
		title.textContent = t('triggers.ransomConfirmSetTitle', 'Confirm Ransom');

		const body = document.createElement('p');
		body.textContent = t(
		  'triggers.ransomConfirmSetBody',
		  'Are you sure you want to set a ransom of {amount} capital?'
		).replace('{amount}', String(amount));

		const buttonContainer = document.createElement('div');
		buttonContainer.className = 'selButMuCon';

		const confirmButton = document.createElement('button');
		confirmButton.className = 'cyber-confirm-btn';
		confirmButton.textContent = t('triggers.confirm', 'Confirm');

		const cancelButton = document.createElement('button');
		cancelButton.className = 'cyber-cancel-btn';
		cancelButton.textContent = t('triggers.cancel', 'Cancel');

		buttonContainer.append(confirmButton, cancelButton);
		popup.append(title, body, buttonContainer);
		document.body.appendChild(popup);

		confirmButton.addEventListener('click', () => {
		  popup.remove();
		  resolve(true);
		}, { once: true });

		cancelButton.addEventListener('click', () => {
		  popup.remove();
		  resolve(false);
		}, { once: true });
	  });
	}


	
export async function payRansom(portfolioId, ransomPoints) {
	  try {
		const confirmPay = await showConfirmPayRansomPopup(ransomPoints);
		if (!confirmPay) return;
	
		const response = await fetch(`${apiBaseUrl}/api/button/payRansom`, {
		  method: 'POST',
		  headers: {
			  'Content-Type': 'application/json',
			  'Authorization': `Bearer ${getAuthToken()}` // Add the token to the Authorization header
			},
		  body: JSON.stringify({ portfolioId, ransomPoints })
		});
	
		if (!response.ok) {
		  const errorData = await response.json();
		  throw new Error(errorData.message || 'Failed to pay ransom');
		}
	
		const result = await response.json();
		showNotification(t('triggers.ransomPaySuccess', 'Ransom paid successfully. Your portfolio is now unlocked.'), true);
		location.reload();
		} catch (error) {
		  console.error('Error paying ransom:', error);
		  showNotification(`${t('triggers.ransomPayErrorPrefix', 'Error: Could not pay ransom.')} ${error.message}`, false);
		}
	}
	
	function showConfirmPayRansomPopup(amount) {
	  return new Promise((resolve) => {
		const popup = document.createElement('div');
		popup.id = 'multiUserSelectionPopup';
	
		const title = document.createElement('h2');
		title.textContent = t('triggers.ransomPayTitle', 'Confirm Pay Ransom');
	
		const body = document.createElement('p');
		body.textContent = t(
		  'triggers.ransomPayBody',
		  'Are you sure you want to pay {amount} capital to unlock your portfolio?'
		).replace('{amount}', amount);
	
		const buttonContainer = document.createElement('div');
		Object.assign(buttonContainer.style, {
		  display: 'flex',
		  justifyContent: 'space-between',
		  marginTop: '20px'
		});
	
		const confirmButton = document.createElement('button');
		confirmButton.textContent = t('triggers.confirm', 'Confirm');
	
		const cancelButton = document.createElement('button');
		cancelButton.textContent = t('triggers.cancel', 'Cancel');
	
		buttonContainer.append(confirmButton, cancelButton);
		popup.append(title, body, buttonContainer);
		document.body.appendChild(popup);
	
		confirmButton.addEventListener('click', () => {
		  popup.remove();
		  resolve(true);
		}, { once: true });
	
		cancelButton.addEventListener('click', () => {
		  popup.remove();
		  resolve(false);
		}, { once: true });
	  });
	}

	
	function showConfirmGiveAwayPopup() {
	  return new Promise((resolve) => {
		const popup = document.createElement('div');
		popup.id = 'confirmGiveAwayPopup';
	
		const title = document.createElement('h2');
		title.textContent = t('triggers.giveawayTitle', 'Confirm Give Away');
	
		const body = document.createElement('p');
		body.textContent = t('triggers.giveawayBody', 'Are you sure you want to give away this commodity?');
	
		const buttonContainer = document.createElement('div');
		Object.assign(buttonContainer.style, {
		  display: 'flex',
		  justifyContent: 'space-between',
		  marginTop: '20px'
		});
	
		const confirmButton = document.createElement('button');
		confirmButton.textContent = t('triggers.confirm', 'Confirm');
	
		const cancelButton = document.createElement('button');
		cancelButton.textContent = t('triggers.cancel', 'Cancel');
	
		buttonContainer.append(confirmButton, cancelButton);
		popup.append(title, body, buttonContainer);
		document.body.appendChild(popup);
	
		confirmButton.addEventListener('click', () => {
		  popup.remove();
		  resolve(true);
		}, { once: true });
	
		cancelButton.addEventListener('click', () => {
		  popup.remove();
		  resolve(false);
		}, { once: true });
	  });
	}


	
	function showMultiUserSelectionPopup(users) {
	  return new Promise((resolve) => {
		const popup = document.createElement('div');
		popup.id = 'multiUserSelectionPopup';
	
		const title = document.createElement('h2');
		title.textContent = t('triggers.communitySelectUsersTitle', 'Select users for the community:');
	
		const listContainer = document.createElement('div');
		listContainer.id = 'userList';
		Object.assign(listContainer.style, { maxHeight: '200px', overflowY: 'auto' });
	
		(users || []).forEach(u => {
		  const label = document.createElement('label');
		  Object.assign(label.style, { display: 'block', marginBottom: '5px' });
	
		  const checkbox = document.createElement('input');
		  checkbox.type = 'checkbox';
		  checkbox.value = u._id;
		  Object.assign(checkbox.style, { marginRight: '5px' });
	
		  const name = u?.name || u?.username || u?.displayName || t('triggers.unknownUser', 'Unknown user');
		  label.append(checkbox, name);
		  listContainer.appendChild(label);
		});
	
		const buttonContainer = document.createElement('div');
		buttonContainer.className = 'selButMuCon';
	
		const confirmButton = document.createElement('button');
		confirmButton.className = 'cyber-confirm-btn'; // Add this line
		confirmButton.textContent = t('triggers.confirm', 'Confirm');
	
		const cancelButton = document.createElement('button');
		cancelButton.className = 'cyber-cancel-btn'; // Add this line
		cancelButton.textContent = t('triggers.cancel', 'Cancel');
	
		buttonContainer.append(confirmButton, cancelButton);
		popup.append(title, listContainer, buttonContainer);
		document.body.appendChild(popup);
	
		confirmButton.addEventListener('click', () => {
		  const selectedUserIds = Array.from(listContainer.querySelectorAll('input:checked')).map(input => input.value);
		  popup.remove();
		  resolve(selectedUserIds);
		}, { once: true });
	
		cancelButton.addEventListener('click', () => {
		  popup.remove();
		  resolve(null);
		}, { once: true });
	  });
	}


	
function playPubVideo(mainVideoUrl, introVideoUrl) {
	  console.log('Attempting to play pub video');
	  console.log('Intro URL:', introVideoUrl);
	  console.log('Main URL:', mainVideoUrl);
	
	  const videoOverlay = document.createElement('div');
	  videoOverlay.setAttribute("style", `
		position: fixed;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		background-color: black;
		z-index: 10000;
		display: flex;
		flex-direction: column;
		justify-content: center;
		align-items: center;
	  `);
	
	  const videoContainer = document.createElement('div');
	  videoContainer.setAttribute("style", `
		width: 100%;
		height: calc(100% - 60px); /* Adjust height to leave space for buttons */
		position: relative;
	  `);
	
	  const videoElement = document.createElement('video');
	  videoElement.setAttribute("style", `
		width: 100%;
		height: 100%;
		object-fit: contain;
	  `);
	  videoElement.controls = true;
	  videoElement.autoplay = true;
	  videoElement.muted = true;
	  videoElement.playsInline = true; // Prevent automatic fullscreen on mobile
	
	  videoContainer.appendChild(videoElement);
	  videoOverlay.appendChild(videoContainer);
	  document.body.appendChild(videoOverlay);
	
	  const buttonContainer = document.createElement('div');
	  buttonContainer.setAttribute("style", `
		position: absolute;
		bottom: 10px;
		left: 0;
		width: 100%;
		display: flex;
		justify-content: space-around;
		z-index: 10001;
	  `);
	
	  const buttonStyle = `
		padding: 10px 20px;
		cursor: pointer;
		font-size: 1rem;
		font-weight: 700;
		text-align: center;
		border: 0.15em solid white;
		border-radius: 0.45em;
		background: rgba(0, 0, 0, 0.5);
		color: white;
	  `;
	
	  const unmuteButton = document.createElement('button');
	  unmuteButton.textContent = 'Unmute';
	  unmuteButton.setAttribute("style", buttonStyle);
	
	  const repeatButton = document.createElement('button');
	  repeatButton.textContent = 'Replay';
	  repeatButton.setAttribute("style", buttonStyle);
	
	  unmuteButton.onclick = function(event) {
		event.stopPropagation();
		videoElement.muted = false;
		unmuteButton.style.display = 'none';
	  };
	
	  repeatButton.onclick = function(event) {
		event.stopPropagation();
		playVideo(mainVideoUrl);
	  };
	
	  buttonContainer.appendChild(unmuteButton);
	  buttonContainer.appendChild(repeatButton);
	  videoOverlay.appendChild(buttonContainer);
	
	  function closeOverlay() {
		if (videoElement.ended) {
		  document.body.removeChild(videoOverlay);
		}
	  }
	
	  function playVideo(introUrl, mainUrl) {
		if (Hls.isSupported()) {
		  console.log('HLS is supported');
		  const hls = new Hls();
		  let currentlyPlaying = 'intro';
	
		  hls.loadSource(introUrl);
		  hls.attachMedia(videoElement);
	
		  hls.on(Hls.Events.MANIFEST_PARSED, function() {
			console.log('HLS manifest parsed, attempting to play intro');
			videoElement.play()
			  .then(() => console.log('Intro video playback started successfully'))
			  .catch(error => {
				console.error('Error playing intro video:', error);
			  });
		  });
	
		  videoElement.addEventListener('ended', function() {
			if (currentlyPlaying === 'intro') {
			  console.log('Intro ended, playing main video');
			  hls.loadSource(mainUrl);
			  currentlyPlaying = 'main';
			} else {
			  closeOverlay();
			}
		  });
	
		  hls.on(Hls.Events.ERROR, function(event, data) {
			console.error('HLS error:', event, data);
		  });
		} else if (videoElement.canPlayType('application/vnd.apple.mpegurl')) {
		  console.log('Native HLS playback supported');
		  videoElement.src = introUrl;
		  videoElement.addEventListener('loadedmetadata', function() {
			console.log('Intro video metadata loaded, attempting to play');
			videoElement.play()
			  .then(() => console.log('Intro video playback started successfully'))
			  .catch(error => {
				console.error('Error playing intro video:', error);
			  });
		  });
	
		  videoElement.addEventListener('ended', function() {
			if (videoElement.src === introUrl) {
			  console.log('Intro ended, playing main video');
			  videoElement.src = mainUrl;
			  videoElement.play();
			} else {
			  closeOverlay();
			}
		  });
		} else {
		  console.error('HLS is not supported in this browser');
		  const fallbackMessage = document.createElement('div');
		  fallbackMessage.textContent = 'Video playback is not supported in this browser';
		  fallbackMessage.style.color = '#ffffff';
		  videoOverlay.appendChild(fallbackMessage);
		}
	  }
	
	  videoElement.addEventListener('error', function(e) {
		console.error('Error with pub video playback:', e);
	  });
	
	  playVideo(introVideoUrl, mainVideoUrl);
	}
	
	function playZnewsVideo(mainVideoUrl, introVideoUrl) {
	  console.log('Attempting to play Znews video');
	  console.log('Intro URL:', introVideoUrl);
	  console.log('Main URL:', mainVideoUrl);
	
	  const videoOverlay = document.createElement('div');
	  videoOverlay.setAttribute("style", `
		position: fixed;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		background-color: black;
		z-index: 10000;
		display: flex;
		flex-direction: column;
		justify-content: center;
		align-items: center;
	  `);
	
	  const videoContainer = document.createElement('div');
	  videoContainer.setAttribute("style", `
		width: 100%;
		height: calc(100% - 60px); /* Adjust height to leave space for buttons */
		position: relative;
	  `);
	
	  const videoElement = document.createElement('video');
	  videoElement.setAttribute("style", `
		width: 100%;
		height: 100%;
		object-fit: contain;
	  `);
	  videoElement.controls = true;
	  videoElement.autoplay = true;
	  videoElement.muted = true;
	  videoElement.playsInline = true; // Prevent automatic fullscreen on mobile
	
	  videoContainer.appendChild(videoElement);
	  videoOverlay.appendChild(videoContainer);
	  document.body.appendChild(videoOverlay);
	
	  const buttonContainer = document.createElement('div');
	  buttonContainer.setAttribute("style", `
		position: absolute;
		bottom: 10px;
		left: 0;
		width: 100%;
		display: flex;
		justify-content: space-around;
		z-index: 10001;
	  `);
	
	  const buttonStyle = `
		padding: 10px 20px;
		cursor: pointer;
		font-size: 1rem;
		font-weight: 700;
		text-align: center;
		border: 0.15em solid white;
		border-radius: 0.45em;
		background: rgba(0, 0, 0, 0.5);
		color: white;
	  `;
	
	  const unmuteButton = document.createElement('button');
	  unmuteButton.textContent = 'Unmute';
	  unmuteButton.setAttribute("style", buttonStyle);
	
	  const repeatButton = document.createElement('button');
	  repeatButton.textContent = 'Replay';
	  repeatButton.setAttribute("style", buttonStyle);
	
	  unmuteButton.onclick = function(event) {
		event.stopPropagation();
		videoElement.muted = false;
		unmuteButton.style.display = 'none';
	  };
	
	  repeatButton.onclick = function(event) {
		event.stopPropagation();
		playVideo(mainVideoUrl);
	  };
	
	  buttonContainer.appendChild(unmuteButton);
	  buttonContainer.appendChild(repeatButton);
	  videoOverlay.appendChild(buttonContainer);
	
	  function closeOverlay() {
		if (videoElement.ended) {
		  document.body.removeChild(videoOverlay);
		}
	  }
	
	  function playVideo(introUrl, mainUrl) {
		if (Hls.isSupported()) {
		  console.log('HLS is supported');
		  const hls = new Hls();
		  let currentlyPlaying = 'intro';
	
		  hls.loadSource(introUrl);
		  hls.attachMedia(videoElement);
	
		  hls.on(Hls.Events.MANIFEST_PARSED, function() {
			console.log('HLS manifest parsed, attempting to play intro');
			videoElement.play()
			  .then(() => console.log('Intro video playback started successfully'))
			  .catch(error => {
				console.error('Error playing intro video:', error);
			  });
		  });
	
		  videoElement.addEventListener('ended', function() {
			if (currentlyPlaying === 'intro') {
			  console.log('Intro ended, playing main video');
			  hls.loadSource(mainUrl);
			  currentlyPlaying = 'main';
			} else {
			  closeOverlay();
			}
		  });
	
		  hls.on(Hls.Events.ERROR, function(event, data) {
			console.error('HLS error:', event, data);
		  });
		} else if (videoElement.canPlayType('application/vnd.apple.mpegurl')) {
		  console.log('Native HLS playback supported');
		  videoElement.src = introUrl;
		  videoElement.addEventListener('loadedmetadata', function() {
			console.log('Intro video metadata loaded, attempting to play');
			videoElement.play()
			  .then(() => console.log('Intro video playback started successfully'))
			  .catch(error => {
				console.error('Error playing intro video:', error);
			  });
		  });
	
		  videoElement.addEventListener('ended', function() {
			if (videoElement.src === introUrl) {
			  console.log('Intro ended, playing main video');
			  videoElement.src = mainUrl;
			  videoElement.play();
			} else {
			  closeOverlay();
			}
		  });
		} else {
		  console.error('HLS is not supported in this browser');
		  const fallbackMessage = document.createElement('div');
		  fallbackMessage.textContent = 'Video playback is not supported in this browser';
		  fallbackMessage.style.color = '#ffffff';
		  videoOverlay.appendChild(fallbackMessage);
		}
	  }
	
	  videoElement.addEventListener('error', function(e) {
		console.error('Error with Znews video playback:', e);
	  });
	
	  playVideo(introVideoUrl, mainVideoUrl);
	}
	
// ===== ITEM STOLEN NOTIFICATION =====
function handleItemStolen(data) {
  const { message, botName, stolenItemName } = data;

  const notificationPopup = document.createElement('div');
  notificationPopup.className = 'subsidy-popup';

  const messageDiv = document.createElement('div');
  messageDiv.style.cssText = 'color: #ff4d4d; font-weight: bold; margin-bottom: 10px; text-shadow: 0 0 5px #ff4d4d;';
  messageDiv.textContent = message;

  notificationPopup.appendChild(messageDiv);

  const closeButton = document.createElement('button');
  closeButton.textContent = t('triggers.close', 'Close');
  closeButton.className = 'close-button-subsidy';
  closeButton.style.marginTop = '10px';

  closeButton.addEventListener('click', () => {
    if (notificationPopup.parentNode) {
      document.body.removeChild(notificationPopup);
    }
  });

  notificationPopup.appendChild(closeButton);
  document.body.appendChild(notificationPopup);
}

// ===== SPECIAL ACTION NOTIFICATION (Generic) =====
function handleSpecialActionNotification(data) {
  const { type, message, details } = data;

  // For Subsidy_Success, always show detailed popup with recipient information
  if (type === 'Subsidy_Success' && details) {
    const notificationPopup = document.createElement('div');
    notificationPopup.className = 'subsidy-popup';
    notificationPopup.style.maxHeight = '70vh';
    notificationPopup.style.overflowY = 'auto';

    let content = `<div style="font-weight: bold; color: #3dd202; margin-bottom: 15px; font-size: 1.1em;">✅ ${message}</div>`;

    // Show social score boost prominently
    if (details.lifeProfitBoost) {
      content += `<div style="background: rgba(61, 210, 2, 0.2); padding: 12px; border-radius: 8px; margin-bottom: 15px; border: 2px solid #3dd202;">
        <div style="font-weight: bold; color: #3dd202; margin-bottom: 8px;">Social Score Boost: +${details.lifeProfitBoost}</div>
        <div style="color: #aaa; font-size: 0.9em;">New Life Profit: ${details.newLifeProfit || 'N/A'}</div>
        <div style="color: #bbb; font-size: 0.85em; margin-top: 5px; font-style: italic;">You helped ${details.recipientCount || 0} people - your positive social impact has been recorded!</div>
      </div>`;
    }

    // Show what was redistributed per person
    if (details.amountPerRecipient !== undefined || details.zollarsPerRecipient !== undefined) {
      content += `<div style="margin-bottom: 15px;">
        <div style="font-weight: bold; margin-bottom: 8px;"> Distributed per recipient:</div>
        <ul style="list-style: none; padding-left: 10px;">`;

      if (details.amountPerRecipient > 0) {
        content += `<li style="margin: 5px 0;"> Capital: <span style="color: #3dd202; font-weight: bold;">+${details.amountPerRecipient.toFixed(2)}</span></li>`;
      }
      if (details.zollarsPerRecipient > 0) {
        content += `<li style="margin: 5px 0;">Ƶ Zollars: <span style="color: #3dd202; font-weight: bold;">+${details.zollarsPerRecipient.toFixed(2)}</span></li>`;
      }
      content += `</ul></div>`;
    }

    // Show recipient list
    if (details.recipientList && details.recipientList.length > 0) {
      content += `<div style="margin-top: 15px; padding-top: 15px; border-top: 1px solid #444;">
        <div style="font-weight: bold; margin-bottom: 8px;"> Beneficiaries (${details.recipientList.length}):</div>
        <div style="max-height: 200px; overflow-y: auto; background: rgba(0,0,0,0.3); padding: 10px; border-radius: 5px;">
          <ul style="list-style: none; padding: 0; margin: 0;">`;

      details.recipientList.forEach(name => {
        content += `<li style="padding: 4px 0; color: #3dd202;">✓ ${name}</li>`;
      });

      content += `</ul></div></div>`;
    }

    // Show target info
    if (details.targetName) {
      content += `<div style="margin-top: 10px; font-size: 0.9em; color: #888;">
        <em>Wealth redistributed from: ${details.targetName}</em>
      </div>`;
    }

    notificationPopup.innerHTML = content;

    const closeButton = document.createElement('button');
    closeButton.textContent = t('triggers.close', 'Close');
    closeButton.className = 'close-button-subsidy';
    closeButton.style.marginTop = '15px';

    closeButton.addEventListener('click', () => {
      if (notificationPopup.parentNode) {
        document.body.removeChild(notificationPopup);
      }
    });

    notificationPopup.appendChild(closeButton);
    document.body.appendChild(notificationPopup);
    return;
  }

  // For other action types, use LED notification or simple popup
  if (typeof showNotification === 'function') {
    // Determine if this is a negative or positive action
    const isNegative = type && (
      type.includes('Target') ||
      type.includes('Steal') ||
      type.includes('LBO') ||
      type.includes('DeletePortfolio') ||
      type.includes('Subsidy_Target')
    );

    // Show LED notification (less intrusive)
    showNotification(message, !isNegative);
  } else {
    // Fallback to popup if showNotification is not available
    const notificationPopup = document.createElement('div');
    notificationPopup.className = 'subsidy-popup';

    let content = `<div style="font-weight: bold; margin-bottom: 10px;">${message}</div>`;

    if (details) {
      content += '<div style="margin-top: 10px; font-size: 0.9em; color: #00ff00;">';
      if (details.executorName) content += `<p style="margin: 5px 0;">Executor: ${details.executorName}</p>`;
      if (details.impact) content += `<p style="margin: 5px 0;">Impact: ${details.impact}</p>`;
      content += '</div>';
    }

    notificationPopup.innerHTML = content;

    const closeButton = document.createElement('button');
    closeButton.textContent = t('triggers.close', 'Close');
    closeButton.className = 'close-button-subsidy';
    closeButton.style.marginTop = '10px';

    closeButton.addEventListener('click', () => {
      if (notificationPopup.parentNode) {
        document.body.removeChild(notificationPopup);
      }
    });

    notificationPopup.appendChild(closeButton);
    document.body.appendChild(notificationPopup);
  }
}

export {
	preloadPortfolios,
  initializeStealButton,
  handleStealButtonClick,
  displayAvailableItemsST,
  stealItem,
  initializeDeletePortfolioButton,
  handleDeletePortfolioButtonClick,
  displayDeletePortfolioUpdate,
 initializeLBOButton,
   handleLBOButtonClick,
   showUserSelectionPopupLBO,
   showConfirmLBOPopup,
   showLBOSelectionPopup,
   calculateLBOCost,
   handleLBOUpdateForTarget,
   initializeSubsidyButton,
	 handleSubsidyButtonClick,
	 handleSubsidyUpdateForTarget,
	 handleSubsidyReceived,
  handleRansomButtonClick,
  showConfirmGiveAwayPopup,
  showUserSelectionPopup,
  showMultiUserSelectionPopup,
  playPubVideo,
  playZnewsVideo,
  handleItemStolen,
  handleSpecialActionNotification

};